/**
 * Workspace Company Tab - 회사정보 및 청구정보
 * 
 * - 워크스페이스 회사정보 (workspace.companyInfo)
 * - 청구 정보 (workspace.billingInfo) - 계약한 회사정보
 */

import { Trans } from '@lingui/macro';
import { Building2, User, Mail, MapPin, Pencil, Loader2, Save, FileText, Receipt } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Fire } from '../../../services/FireClient';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { IconHash } from '../../components/FigmaIcons';

interface Props {
    workspaceId: string;
}

interface CompanyInfo {
    companyName?: string;
    representative?: string;
    department?: string;  // 일본 비즈니스 필수
    email?: string;
    phone?: string;
    postalCode?: string;
    address?: string;
    registrationNumber?: string; // 사업자등록번호
}

interface BillingInfo {
    companyName?: string;
    representative?: string;
    department?: string;  // 일본 비즈니스 필수
    email?: string;
    phone?: string;
    postalCode?: string;
    address?: string;
    jp_registrationNumber?: string; // 적격청구서 등록번호 (T + 13자리)
    jp_bankInfo?: string; // 은행 계좌 정보
}

const defaultCompanyInfo = (): CompanyInfo => ({
    companyName: '',
    representative: '',
    department: '',
    email: '',
    phone: '',
    postalCode: '',
    address: '',
    registrationNumber: '',
});

const defaultBillingInfo = (): BillingInfo => ({
    companyName: '',
    representative: '',
    department: '',
    email: '',
    phone: '',
    postalCode: '',
    address: '',
    jp_registrationNumber: '',
    jp_bankInfo: '',
});

// 적격청구서 등록번호 검증 (T + 13자리 숫자)
const validateInvoiceRegistrationNumber = (num: string): boolean => {
    if (!num) return true; // 빈 값은 허용
    return /^T\d{13}$/.test(num);
};

const WorkspaceCompanyTab: React.FC<Props> = ({ workspaceId }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Company Info (워크스페이스 회사정보)
    const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(defaultCompanyInfo());
    const [editedCompanyInfo, setEditedCompanyInfo] = useState<CompanyInfo>(defaultCompanyInfo());
    const [isCompanyDialogOpen, setIsCompanyDialogOpen] = useState(false);

    // Billing Info (청구 정보 / 계약 회사정보)
    const [billingInfo, setBillingInfo] = useState<BillingInfo>(defaultBillingInfo());
    const [editedBillingInfo, setEditedBillingInfo] = useState<BillingInfo>(defaultBillingInfo());
    const [isBillingDialogOpen, setIsBillingDialogOpen] = useState(false);

    useEffect(() => {
        loadData();
    }, [workspaceId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const wsDoc = await getDoc(doc(Fire.store, 'workspaces', workspaceId));
            if (wsDoc.exists()) {
                const data = wsDoc.data();
                if (data.companyInfo) {
                    setCompanyInfo(data.companyInfo);
                    setEditedCompanyInfo(data.companyInfo);
                }
                if (data.billingInfo) {
                    setBillingInfo(data.billingInfo);
                    setEditedBillingInfo(data.billingInfo);
                }
            }
        } catch (e) {
            console.error('Failed to load company data:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCompanyInfo = async () => {
        setSaving(true);
        try {
            await updateDoc(doc(Fire.store, 'workspaces', workspaceId), {
                companyInfo: editedCompanyInfo,
                updatedAt: serverTimestamp(),
            });
            setCompanyInfo(editedCompanyInfo);
            setIsCompanyDialogOpen(false);
            alert('회사정보가 저장되었습니다.');
        } catch (e) {
            console.error('Failed to save company info:', e);
            alert('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveBillingInfo = async () => {
        setSaving(true);
        try {
            await updateDoc(doc(Fire.store, 'workspaces', workspaceId), {
                billingInfo: editedBillingInfo,
                updatedAt: serverTimestamp(),
            });
            setBillingInfo(editedBillingInfo);
            setIsBillingDialogOpen(false);
            alert('청구정보가 저장되었습니다.');
        } catch (e) {
            console.error('Failed to save billing info:', e);
            alert('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // 회사정보 → 청구정보 복사
    const handleCopyCompanyToBilling = () => {
        setEditedBillingInfo({
            ...editedBillingInfo,
            companyName: companyInfo.companyName,
            representative: companyInfo.representative,
            department: companyInfo.department,
            email: companyInfo.email,
            phone: companyInfo.phone,
            postalCode: companyInfo.postalCode,
            address: companyInfo.address,
            // registrationNumber는 복사하지 않음 (형식이 다름)
        });
    };

    const renderInfoFields = (info: CompanyInfo | BillingInfo, type: 'company' | 'billing') => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
                <div className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                        <div className="text-xs text-slate-500">회사명</div>
                        <div className="font-medium text-slate-900">{info.companyName || '-'}</div>
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                        <div className="text-xs text-slate-500">대표자</div>
                        <div className="font-medium text-slate-900">{info.representative || '-'}</div>
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                        <div className="text-xs text-slate-500">이메일</div>
                        <div className="font-medium text-slate-900">{info.email || '-'}</div>
                    </div>
                </div>
            </div>
            <div className="space-y-4">
                <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                        <div className="text-xs text-slate-500">주소</div>
                        <div className="font-medium text-slate-900">
                            {info.postalCode && `〒${info.postalCode}`}
                            {info.postalCode && info.address && <br />}
                            {info.address || (info.postalCode ? '' : '-')}
                        </div>
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <IconHash className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                        <div className="text-xs text-slate-500">
                            {type === 'company' ? '사업자등록번호' : '적격청구서 등록번호'}
                        </div>
                        <div className="font-medium text-slate-900 font-mono">
                            {type === 'company'
                                ? (info as CompanyInfo).registrationNumber || '-'
                                : (info as BillingInfo).jp_registrationNumber || '-'
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderEditForm = (
        editedInfo: CompanyInfo | BillingInfo,
        setEditedInfo: (info: any) => void,
        type: 'company' | 'billing'
    ) => (
        <div className="space-y-4 py-4">
            <div>
                <Label>회사명</Label>
                <Input
                    value={editedInfo.companyName || ''}
                    onChange={(e) => setEditedInfo({ ...editedInfo, companyName: e.target.value })}
                    placeholder="株式会社○○"
                />
            </div>
            <div>
                <Label>대표자명</Label>
                <Input
                    value={editedInfo.representative || ''}
                    onChange={(e) => setEditedInfo({ ...editedInfo, representative: e.target.value })}
                    placeholder="田中 太郎"
                />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>이메일</Label>
                    <Input
                        type="email"
                        value={editedInfo.email || ''}
                        onChange={(e) => setEditedInfo({ ...editedInfo, email: e.target.value })}
                        placeholder="info@example.com"
                    />
                </div>
                <div>
                    <Label>전화번호</Label>
                    <Input
                        value={editedInfo.phone || ''}
                        onChange={(e) => setEditedInfo({ ...editedInfo, phone: e.target.value })}
                        placeholder="03-1234-5678"
                    />
                </div>
            </div>
            <div>
                <Label>우편번호</Label>
                <Input
                    value={editedInfo.postalCode || ''}
                    onChange={(e) => setEditedInfo({ ...editedInfo, postalCode: e.target.value })}
                    placeholder="100-0001"
                />
            </div>
            <div>
                <Label>주소</Label>
                <Input
                    value={editedInfo.address || ''}
                    onChange={(e) => setEditedInfo({ ...editedInfo, address: e.target.value })}
                    placeholder="東京都千代田区..."
                />
            </div>
            <div>
                <Label>{type === 'company' ? '사업자등록번호' : '적격청구서 등록번호 (인보이스 번호)'}</Label>
                <Input
                    value={type === 'company'
                        ? (editedInfo as CompanyInfo).registrationNumber || ''
                        : (editedInfo as BillingInfo).jp_registrationNumber || ''
                    }
                    onChange={(e) => {
                        if (type === 'company') {
                            setEditedInfo({ ...editedInfo, registrationNumber: e.target.value });
                        } else {
                            setEditedInfo({ ...editedInfo, jp_registrationNumber: e.target.value });
                        }
                    }}
                    placeholder={type === 'company' ? '123-45-67890' : 'T1234567890123'}
                />
                {type === 'billing' && (editedInfo as BillingInfo).jp_registrationNumber &&
                    !validateInvoiceRegistrationNumber((editedInfo as BillingInfo).jp_registrationNumber || '') && (
                        <p className="text-xs text-red-500 mt-1">형식: T + 13자리 숫자 (ex: T1234567890123)</p>
                    )}
            </div>
        </div>
    );

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* 워크스페이스 회사정보 */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-blue-500" />
                            워크스페이스 회사정보
                        </CardTitle>
                        <CardDescription>이 워크스페이스를 소유/운영하는 회사 정보</CardDescription>
                    </div>
                    <Dialog open={isCompanyDialogOpen} onOpenChange={setIsCompanyDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setEditedCompanyInfo({ ...companyInfo });
                                    setIsCompanyDialogOpen(true);
                                }}
                            >
                                <Pencil className="w-3.5 h-3.5 mr-2" />
                                편집
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>회사정보 편집</DialogTitle>
                            </DialogHeader>
                            {renderEditForm(editedCompanyInfo, setEditedCompanyInfo, 'company')}
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsCompanyDialogOpen(false)}>
                                    취소
                                </Button>
                                <Button onClick={handleSaveCompanyInfo} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    <Save className="w-4 h-4 mr-2" />
                                    저장
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </CardHeader>
                <CardContent>
                    {companyInfo.companyName ? (
                        renderInfoFields(companyInfo, 'company')
                    ) : (
                        <div className="text-slate-400 text-sm text-center py-4">
                            등록된 회사정보가 없습니다. 편집 버튼을 눌러 등록하세요.
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* 청구 정보 (계약 회사정보) */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Receipt className="w-5 h-5 text-emerald-500" />
                            청구 정보
                        </CardTitle>
                        <CardDescription>청구서 발행을 위한 계약 회사정보 (워크스페이스 회사와 다른 경우)</CardDescription>
                    </div>
                    <Dialog open={isBillingDialogOpen} onOpenChange={setIsBillingDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setEditedBillingInfo({ ...billingInfo });
                                    setIsBillingDialogOpen(true);
                                }}
                            >
                                <Pencil className="w-3.5 h-3.5 mr-2" />
                                편집
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>청구 정보 편집</DialogTitle>
                            </DialogHeader>

                            {/* 회사정보에서 복사 버튼 */}
                            {companyInfo.companyName && (
                                <div className="flex justify-end -mt-2 mb-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopyCompanyToBilling}
                                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                    >
                                        <Trans>Copy from company info</Trans>
                                    </Button>
                                </div>
                            )}

                            {renderEditForm(editedBillingInfo, setEditedBillingInfo, 'billing')}
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsBillingDialogOpen(false)}>
                                    취소
                                </Button>
                                <Button onClick={handleSaveBillingInfo} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    <Save className="w-4 h-4 mr-2" />
                                    저장
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </CardHeader>
                <CardContent>
                    {billingInfo.companyName ? (
                        renderInfoFields(billingInfo, 'billing')
                    ) : (
                        <div className="text-slate-400 text-sm text-center py-4">
                            별도의 청구 정보가 없습니다. 워크스페이스 회사정보로 청구됩니다.
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default WorkspaceCompanyTab;
